import { gte, lte } from 'semver'

type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];
interface PlatformRequirement {
    Android: string
    iOS: string
}

class AppPlatform{
    platformName: keyof PlatformRequirement;
    platformVersion: string | null | undefined;

    constructor(platformName: keyof PlatformRequirement, platformVersion: string | null | undefined){
        this.platformName = platformName
        this.platformVersion = platformVersion
    }
    /**
     * Run a comparison against the current version and return if its an NEWER version.
     * @param version The version you want it to be greater than. Example: {}
     * @returns {boolean} true is current version is greater than the specified one. Note: will return false if
     *  runs on a platform which is not supported. E.g. {Android: 1.0.0} but when runs on iOS will always be false.
     */
    isGreaterOrEqualThan(version: AtLeastOne<PlatformRequirement>): boolean{
        try{
            const platformVersion = version?.[this.platformName]
            if (!platformVersion) return false;
            return gte(this.platformVersion || '', platformVersion)
        }catch(error){
            console.warn(`Failed to parse app version. ${this.platformVersion}`)
            return false
        }
    }
    /**
     * Run a comparison against the current version and return if its an OLDER version.
     * @param version The version you want it to be less than 
     * @returns {boolean} true if current version is less than current
     */
    isLessOrEqualThan(version: AtLeastOne<PlatformRequirement>): boolean{
        try{
            const platformVersion = version?.[this.platformName]
            if (!platformVersion) return false;
            return lte(this.platformVersion || '', platformVersion)
        }catch(error){
            console.warn(`Failed to parse app version. ${this.platformVersion}`)
            return false
        }
    }
    /**
     * Is the platform the iOS app?
     * @returns {boolean}
     */
    isiOS():boolean{
        return this.platformName == "iOS"
    }
    /**
     * Is the platform the android app?
     * @returns {boolean}
     */
    isAndroid(): boolean{
        return this.platformName == "Android"
    }
}

export default class BonusCardApp{
    userAgent: string;
    platform: AppPlatform | null;

    constructor (uaString?: string | undefined){
        this.userAgent = uaString || (typeof window !== 'undefined' ? window?.navigator?.userAgent : '')
        this.platform = this.#getPlatform()
    }

    /**
     * Private Method, called on construction to determine the platform and app version
     * To access this data please use `.platform` instead to save calculation costs
     * @returns {AppPlatform | null} 
     */
    #getPlatform(): AppPlatform | null{
        try{
            const androidUserAgentRegex = /^Iceland(?: QA)*\/([\d.]*).*Android.*/i;
            const iOSUserAgentRegex = /^Iceland(?: QA)*\/([\d.]*) \(iOS (?:[\d.]*)\)/i;
            // In the SSR we don't have a window, but the order details page is rendered on the SSR. So if
            // no window is present just rely on the regex of the UA.
            const windowObj = typeof window !== 'undefined' ? window : {webkit: {}, appInterface: {}}
            // Check if Android OS
            if (androidUserAgentRegex.test(this.userAgent) && 'appInterface' in windowObj && windowObj.appInterface != undefined) {
                const match = androidUserAgentRegex.exec(this.userAgent)
                return new AppPlatform("Android", match?.[1])
            }
            // Check if IOS
            else if (iOSUserAgentRegex.test(this.userAgent) && 'webkit' in windowObj && windowObj.webkit != undefined) { 
                const match = iOSUserAgentRegex.exec(this.userAgent)
                return new AppPlatform("iOS", match?.[1])
            }
            return null
        } catch (e) {
            console.error(e)
            // Not in App, ignore
            return null
        }
    }

    /**
     * Check if request is from bonus card or not
     * @returns {boolean} are you on the app?
     */
    isApp(): boolean {
        return this.platform !== null
    }
}
